import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { boxTypesApi } from './box-types.api';
import { boxTypeKeys } from './box-types.queries';
import type {
  CreateBoxTypeRequest,
  UpdateBoxTypeRequest,
  BoxTypesListParams
} from '../types';

export function useCreateBoxTypeMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('boxTypes');

  return useMutation({
    mutationFn: (data: CreateBoxTypeRequest) => boxTypesApi.create(data),
    meta: {
      operationType: 'create',
      fallbackSuccessMessage: t('createSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: boxTypeKeys.lists() });
    }
  });
}

export function useUpdateBoxTypeMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('boxTypes');

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateBoxTypeRequest }) =>
      boxTypesApi.update(id, data),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('updateSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: boxTypeKeys.lists() });
    }
  });
}

export function useDeleteBoxTypeMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('boxTypes');

  return useMutation({
    mutationFn: ({ id, force }: { id: number; force?: boolean }) =>
      boxTypesApi.delete({ id, force }),
    meta: {
      operationType: 'delete',
      fallbackSuccessMessage: t('deleteSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: boxTypeKeys.lists() });
    }
  });
}

export function useBulkDeleteBoxTypesMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('boxTypes');

  return useMutation({
    mutationFn: (
      params:
        | { ids: number[] }
        | { all: true; filters: Partial<BoxTypesListParams> }
    ) => boxTypesApi.bulkDelete(params),
    meta: {
      operationType: 'delete',
      fallbackSuccessMessage: t('bulkDeleteSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: boxTypeKeys.lists() });
    }
  });
}

export function useBulkChangeStatusBoxTypesMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('boxTypes');

  return useMutation({
    mutationFn: (
      params:
        | { ids: number[]; status: 'active' | 'inactive' }
        | {
            all: true;
            filters: Partial<BoxTypesListParams>;
            status: 'active' | 'inactive';
          }
    ) => boxTypesApi.bulkChangeStatus(params),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('bulkChangeStatusSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: boxTypeKeys.lists() });
    }
  });
}
